import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { Divider, Flex, VStack } from '@televet/kibble-ui/build/chakra';
import { updateAppointmentDraft } from 'pages/Appointments/state/appointmentsModalSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useClipboard } from 'shared/hooks/useClipboard';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { PimsWritebackStatus } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { getTimezoneAbbreviation } from 'shared/utils/timezoneDisplayNames';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { useTracking } from 'shared/providers/TrackingProvider';
import { AiRecapEvents } from 'shared/providers/TrackingProvider/EventNames';

export const AiRecapNotes = (): JSX.Element => {
  const { trackEvent } = useTracking();
  const { copy } = useClipboard();
  const [showNotes, setShowNotes] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const { appointmentId } = useAppSelector((state) => state.appointmentsModal);
  const { recapNotes, aiRecapWriteback } = useAppSelector((state) => state.appointmentsModal.appointmentDraft);
  const { currentClinic } = useClinicUser();
  const { isExportToPimsSupported, primaryIntegrationName } = useIntegrationsProvider();
  const isAiRecapWritebackEnabled = isExportToPimsSupported && currentClinic?.clinicSetting?.aiRecapWritebackEnabled;

  const timezoneAbbreviation = useMemo(
    () => getTimezoneAbbreviation(currentClinic?.timezoneName || 'America/Chicago'),
    [currentClinic?.timezoneName],
  );

  const syncMessage = useMemo(() => {
    if (!(aiRecapWriteback?.status === PimsWritebackStatus.Success)) return;
    return `Synced to ${primaryIntegrationName} at ${new Date(aiRecapWriteback.updatedAt).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })} ${timezoneAbbreviation} on ${new Date(aiRecapWriteback.updatedAt).toLocaleDateString()}`;
  }, [aiRecapWriteback, timezoneAbbreviation, primaryIntegrationName]);

  const dispatch = useAppDispatch();

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(updateAppointmentDraft({ recapNotes: e.target.value }));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!showNotes) {
      setShowNotes(!!recapNotes?.length);
    }
  }, [recapNotes?.length, showNotes]);

  return (
    <VStack className="AppointmentModal__Notes" w="100%" align="flex-start" position="relative">
      {showNotes && (
        <>
          {aiRecapWriteback?.status === PimsWritebackStatus.Success && (
            <Flex width="100%" justify="flex-end" mt={2} mb={-4}>
              <Icon name="checkmark" size="xs" variant="subtle" mr={1} />
              <Text variant="subtle" size="xs">
                {syncMessage}
              </Text>
            </Flex>
          )}
          <Textarea
            value={recapNotes}
            onChange={handleOnChange}
            mt={4}
            labelStyle={{ fontWeight: 'bold' }}
            data-testid="ai-recap-notes-textarea"
          />

          <Flex justifyContent="space-between" w="100%">
            <Flex alignItems="center">
              <Text as="p" size="sm" variant="subtle" mr={1}>
                {feedbackSubmitted ? 'Thanks for your feedback!' : 'How was this AI Recap?'}
              </Text>
              {!feedbackSubmitted && (
                <>
                  <Button
                    iconName="thumbsUp"
                    size="sm"
                    variant="ghostNeutral"
                    id="ai-recap-positive-feedback"
                    onClick={(): void => {
                      trackEvent(AiRecapEvents.PositiveFeedback, { appointmentId });
                      setFeedbackSubmitted(true);
                    }}
                  />
                  <Button
                    iconName="thumbsDown"
                    size="sm"
                    variant="ghostNeutral"
                    id="ai-recap-negative-feedback"
                    onClick={(): void => {
                      trackEvent(AiRecapEvents.NegativeFeedback, { appointmentId });
                      setFeedbackSubmitted(true);
                    }}
                  />
                </>
              )}
            </Flex>
            {!!recapNotes?.length && (
              <Button onClick={(): void => copy(recapNotes)} variant="secondarySubtle" iconName="twoSquares" size="sm">
                Copy Notes
              </Button>
            )}
          </Flex>
          {isAiRecapWritebackEnabled && !aiRecapWriteback && (
            <Alert
              status="info"
              description={`AI Recap Notes and any edits made to it will sync to ${primaryIntegrationName} nightly.`}
              hideCloseButton
            />
          )}
          <Divider />
        </>
      )}
    </VStack>
  );
};
